import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  input,
  signal,
  computed,
  ContentChildren,
  QueryList,
  ElementRef,
  AfterContentInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { ButtonComponent } from '../../button/button.component';
import { LayoutService } from '../../../../core/services/layout.service';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, LucideIconComponent, ButtonComponent],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.css'],
})
export class ModalComponent implements OnDestroy, AfterContentInit {
  // New signal-based inputs
  readonly title = input<string>('');
  readonly subtitle = input<string>('');
  readonly icon = input<string>('');
  readonly size = input<'sm' | 'md' | 'lg' | 'xl' | 'full'>('md');
  readonly zIndex = input<number>(50);
  readonly managedByHost = input<boolean>(false);

  private readonly renderer = inject(Renderer2);
  private readonly layoutService = inject(LayoutService);

  private _isOpen = false;

  @Input()
  set isOpen(value: boolean) {
    this._isOpen = value;
    if (value) {
      this.renderer.addClass(document.body, 'overflow-hidden');
    } else {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }
  get isOpen(): boolean {
    return this._isOpen;
  }

  @Output() closed = new EventEmitter<void>();

  @ContentChildren('action') actionElements!: QueryList<ElementRef>;

  // Check if mobile
  protected readonly isMobile = this.layoutService.isMobile;

  // Computed properties for styling
  modalSizeClasses = computed(() => {
    // On mobile, always go full screen
    if (this.isMobile()) {
      return 'w-full h-full max-w-full max-h-full rounded-none';
    }

    // Desktop size configurations - much wider!
    const sizeMap = {
      sm: 'max-w-xl max-h-[85vh]', // Increased from max-w-md
      md: 'max-w-3xl max-h-[90vh]', // Increased from max-w-lg
      lg: 'max-w-5xl max-h-[92vh]', // Increased from max-w-2xl
      xl: 'max-w-7xl max-h-[95vh]', // Increased from max-w-4xl
      full: 'max-w-[95vw] h-[95vh]', // Same
    };

    return `${sizeMap[this.size()]} w-full`;
  });

  // Computed padding classes based on screen size
  modalPaddingClasses = computed(() => {
    if (this.isMobile()) {
      return 'px-4'; // Minimal padding on mobile
    }
    return 'px-6'; // Standard padding on desktop
  });

  // Computed backdrop padding
  backdropPaddingClasses = computed(() => {
    if (this.isMobile()) {
      return 'p-0'; // No padding on mobile for full screen
    }
    return 'p-4'; // Standard padding on desktop
  });

  hasActions = signal(false);

  ngAfterContentInit() {
    // Check if there are action elements and update the signal
    const hasActionElements =
      this.actionElements && this.actionElements.length > 0;
    this.hasActions.set(hasActionElements);
  }

  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    this.close();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  close(): void {
    if (!this.managedByHost()) {
      this.isOpen = false;
    }
    this.closed.emit();
  }

  ngOnDestroy(): void {
    // Ensure the class is removed when the component is destroyed.
    this.renderer.removeClass(document.body, 'overflow-hidden');
  }
}
